import React, { useEffect, useState } from 'react';
import {
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Typography,
  Box,
  Collapse,
  IconButton,
  Pagination,
  Stack,
  Select,
  MenuItem,
  FormControl,
  SelectChangeEvent,
  Avatar,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, KeyboardArrowDown, KeyboardArrowUp, Edit as EditIcon, CheckCircleOutline as CheckCircleOutlineIcon, CheckCircle as CheckCircleIcon, Cancel as CancelIcon, ContentCopy as ContentCopyIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axios';
import { UserRequest, RequestItem, PointOfSale } from '../types/types';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateForDisplay } from '../utils/dateUtils';
import { formatPhoneNumber } from '../utils/formatUtils';
import { t } from '../utils/translations';
import { useTheme } from '@mui/material/styles';
import { useMediaQuery } from '@mui/material';

interface RowProps {
  request: UserRequest;
}

interface GroupedItems {
  [category: string]: {
    categoryName: string;
    items: RequestItem[];
  };
}

interface PaginatedResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: UserRequest[];
}

const RequestRow = ({ request }: RowProps) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const isFranchise = user?.type === 'franchise';
  const isHoreca = user?.type === 'horeca';

  if (!request || !request.items) {
    return null;
  }

  // Group items by category
  const groupedItems = request.items.reduce<GroupedItems>((acc, item) => {
    if (!acc[item.category_name]) {
      acc[item.category_name] = {
        categoryName: item.category_name,
        items: []
      };
    }
    acc[item.category_name].items.push(item);
    return acc;
  }, {});

  const handleEdit = () => {
    if (request && request.id) {
      try {
        navigate(`/requests/${request.id}/edit`);
      } catch (error) {
        console.error('Navigation error:', error);
      }
    } else {
      console.error('Invalid request or request ID:', request);
    }
  };

  const handleAccept = () => {
    navigate(`/requests/${request.id}/accept`);
  };

  const handleDuplicate = () => {
    navigate('/requests/new', { 
      state: { duplicateRequest: request } 
    });
  };

  // Get status color
  const getStatusColor = () => {
    if (request.status === 'fully_accepted') return 'success.main';
    if (request.status === 'partially_accepted') return 'warning.main';
    if (request.status_display === 'Одобрен') return 'success.main';
    return 'info.main';
  };

  const getStatusBgColor = () => {
    if (request.status === 'fully_accepted') return 'rgba(84, 214, 44, 0.16)';
    if (request.status === 'partially_accepted') return 'rgba(255, 171, 0, 0.16)';
    if (request.status_display === 'Одобрен') return 'rgba(84, 214, 44, 0.16)';
    return 'rgba(24, 144, 255, 0.16)';
  };

  return (
    <>
      <TableRow 
        key={`main-${request.id}`}
        sx={{ 
          '&:hover': { 
            backgroundColor: 'rgba(0, 0, 0, 0.02)' 
          },
          cursor: 'pointer',
          transition: 'background-color 0.2s'
        }}
        onClick={() => setOpen(!open)}
      >
        <TableCell sx={{ width: { xs: '40px', sm: '48px' } }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setOpen(!open);
            }}
            sx={{ 
              color: open ? '#ec248f' : 'action.active',
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'none'
            }}
          >
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ width: { xs: '60px', sm: '80px' } }}>
          <Typography variant="body2" fontWeight="medium">
            #{request.id}
          </Typography>
        </TableCell>
        <TableCell 
          sx={{ 
            width: { xs: '100px', sm: '120px' },
            padding: { xs: '12px 8px', sm: '16px 16px' },
            verticalAlign: 'middle'
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              textAlign: 'center',
              color: getStatusColor(),
              bgcolor: getStatusBgColor(),
              px: { xs: 1, sm: 1.5 },
              py: 0.5,
              borderRadius: 1,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              whiteSpace: 'nowrap',
              fontWeight: 'medium'
            }}
          >
            {request.status_display}
          </Box>
          
          {/* Mobile-only date display */}
          {isMobile && (
            <Typography 
              variant="caption" 
              display="block" 
              sx={{ 
                mt: 1, 
                color: 'text.secondary',
                fontSize: '0.7rem'
              }}
            >
              {request.created_at ? formatDate(request.created_at) : request.created_at_formatted}
            </Typography>
          )}
        </TableCell>
        <TableCell 
          sx={{ 
            width: { xs: '120px', sm: '160px' },
            display: { xs: 'none', sm: 'table-cell' }
          }}
        >
          {request.created_at ? formatDate(request.created_at) : request.created_at_formatted}
        </TableCell>
        {isFranchise && (
          <TableCell
            sx={{
              width: { xs: '120px', sm: '160px' },
              display: { xs: 'none', sm: 'table-cell' }
            }}
          >
            {request.shipping_date ? formatDateForDisplay(request.shipping_date) : '-'}
          </TableCell>
        )}
        {isHoreca && (
          <TableCell
            sx={{
              width: { xs: '120px', sm: '160px' },
              display: { xs: 'none', sm: 'table-cell' }
            }}
          >
            {t(request.type_of_delivery || '-')}
          </TableCell>
        )}
        <TableCell 
          sx={{ 
            display: { xs: 'none', md: 'table-cell' },
            color: 'text.secondary',
            fontSize: '0.875rem'
          }}
        >
          <Typography 
            variant="body2" 
            sx={{ 
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              lineHeight: '1.4em',
              maxHeight: '2.8em'
            }}
          >
            {request.comment || '-'}
          </Typography>
        </TableCell>
        <TableCell 
          sx={{ 
            width: { xs: '80px', sm: '100px' }, 
            textAlign: 'center' 
          }}
        >
          {request.status_display && (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDuplicate();
                }}
                title={t('Duplicate Request')}
                sx={{ 
                  color: '#ec248f',
                  '&:hover': { 
                    backgroundColor: 'rgba(236, 36, 143, 0.08)' 
                  }
                }}
              >
                <ContentCopyIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
              {request.status !== 'printed' && (
                <IconButton
                  color="primary"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit();
                  }}
                  title={t('Edit Request')}
                  data-testid={`edit-button-${request.id}`}
                  sx={{ 
                    color: '#ec248f',
                    '&:hover': { 
                      backgroundColor: 'rgba(236, 36, 143, 0.08)' 
                    }
                  }}
                >
                  <EditIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              )}
              {request.status === 'printed' && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAccept();
                  }}
                  title={t('Accept Request')}
                  sx={{ 
                    color: 'success.main',
                    '&:hover': { 
                      backgroundColor: 'rgba(84, 214, 44, 0.08)' 
                    },
                    display: isFranchise || isHoreca ? 'none' : 'inline-flex'
                  }}
                >
                  <CheckCircleOutlineIcon fontSize={isMobile ? "small" : "medium"} />
                </IconButton>
              )}
            </Box>
          )}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0, border: 'none' }} colSpan={6}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ my: 2, mx: { xs: 1, sm: 3 }, borderRadius: 2, overflow: 'hidden', bgcolor: 'background.paper' }}>
              {/* Request Details Section */}
              <Box sx={{ px: 3, py: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                {/* Shipping Date (for franchise) */}
                {isFranchise && request.shipping_date && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('Shipping Date')}:
                    </Typography>
                    <Typography variant="body2">
                      {formatDateForDisplay(request.shipping_date)}
                    </Typography>
                  </Box>
                )}
                
                {/* Type of Delivery (for horeca) */}
                {isHoreca && request.type_of_delivery && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('Type of Delivery')}:
                    </Typography>
                    <Typography variant="body2">
                      {t(request.type_of_delivery.charAt(0).toUpperCase() + request.type_of_delivery.slice(1))}
                    </Typography>
                  </Box>
                )}

                {/* Shipping Address */}
                {request.shipping_address && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('Shipping Address')}:
                    </Typography>
                    <Typography variant="body2">
                      {request.shipping_address}
                    </Typography>
                  </Box>
                )}

                {/* Document Type (for horeca) */}
                {isHoreca && request.document_type && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('Document Type')}:
                    </Typography>
                    <Typography variant="body2">
                      {t(request.document_type === 'invoice' ? 'Invoice' : 'Waybill')}
                    </Typography>
                  </Box>
                )}
                
                {/* Comment */}
                {request.comment && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('Comment')}:
                    </Typography>
                    <Typography variant="body2">
                      {request.comment}
                    </Typography>
                  </Box>
                )}
              </Box>
              
              {Object.values(groupedItems).map((group) => (
                <Box key={`group-${group.categoryName}`} sx={{ mb: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{
                      bgcolor: 'rgba(236, 36, 143, 0.08)',
                      color: 'rgba(236, 36, 143, 0.8)',
                      p: { xs: 1, sm: 1.5 },
                      mb: 1,
                      fontWeight: 'bold',
                      borderRadius: 1
                    }}
                  >
                    {group.categoryName}
                  </Typography>
                  <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table 
                      size="small" 
                      sx={{ 
                        '& .MuiTableCell-root': { 
                          py: { xs: 1, sm: 1.5 },
                          px: { xs: 1, sm: 2 },
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        } 
                      }}
                    >
                      <TableHead>
                        <TableRow sx={{ 
                          backgroundColor: 'rgba(0, 0, 0, 0.02)',
                          '& th': { fontWeight: 'bold' }
                        }}>
                          <TableCell sx={{ width: '45%' }}>{t('Product')}</TableCell>
                          <TableCell sx={{ width: '15%' }}>{t('Quantity')}</TableCell>
                          <TableCell sx={{ 
                            width: '30%',
                            display: { xs: 'none', sm: 'table-cell' }
                          }}>
                            {t('Comment')}
                          </TableCell>
                          <TableCell sx={{ width: '10%' }}>{t('Status')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {group.items.map((item, index) => (
                          <TableRow key={`item-${item.id || `${group.categoryName}-${index}`}`}>
                            <TableCell sx={{ width: '45%' }}>
                              <Typography variant="body2" fontWeight="medium">
                                {item.product_name}
                              </Typography>
                              
                              {/* Mobile-only comment display */}
                              {isMobile && item.acceptance_comment && (
                                <Typography 
                                  variant="caption" 
                                  display="block" 
                                  sx={{ 
                                    mt: 0.5, 
                                    color: 'text.secondary',
                                    fontSize: '0.7rem'
                                  }}
                                >
                                  {item.acceptance_comment}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ width: '15%' }}>
                              <Chip 
                                label={item.quantity} 
                                size="small" 
                                variant="outlined"
                                sx={{ 
                                  height: 24,
                                  fontSize: '0.75rem',
                                  borderColor: 'rgba(236, 36, 143, 0.3)',
                                  color: '#ec248f'
                                }}
                              />
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                width: '30%',
                                color: 'text.secondary',
                                display: { xs: 'none', sm: 'table-cell' }
                              }}
                            >
                              {item.acceptance_comment || '-'}
                            </TableCell>
                            <TableCell sx={{ width: '10%', textAlign: 'center' }}>
                              {request.status === 'fully_accepted' || request.status === 'partially_accepted' ? (
                                item.accepted ? (
                                  <CheckCircleIcon color="success" fontSize="small" />
                                ) : (
                                  <CancelIcon color="error" fontSize="small" />
                                )
                              ) : "-"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

const EmptyStateContent = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
    <Box sx={{ 
      width: 80, 
      height: 80, 
      borderRadius: '50%', 
      bgcolor: 'rgba(236, 36, 143, 0.1)', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center',
      mb: 2
    }}>
      <Typography variant="h5" sx={{ color: '#ec248f' }}>
        0
      </Typography>
    </Box>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
      {t('No requests found')}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {t('Create a new request to get started')}
    </Typography>
  </Box>
);

const RequestCard = ({ request }: RowProps) => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isFranchise = user?.type === 'franchise';
  const isHoreca = user?.type === 'horeca';
  
  if (!request || !request.items) {
    return null;
  }

  // Group items by category
  const groupedItems = request.items.reduce<GroupedItems>((acc, item) => {
    if (!acc[item.category_name]) {
      acc[item.category_name] = {
        categoryName: item.category_name,
        items: []
      };
    }
    acc[item.category_name].items.push(item);
    return acc;
  }, {});

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card expansion
    if (request && request.id) {
      navigate(`/requests/${request.id}/edit`);
    }
  };

  const handleAccept = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card expansion
    navigate(`/requests/${request.id}/accept`);
  };

  const handleDuplicate = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card expansion
    navigate('/requests/new', { 
      state: { duplicateRequest: request } 
    });
  };

  // Get status color
  const getStatusColor = () => {
    if (request.status === 'fully_accepted') return 'success.main';
    if (request.status === 'partially_accepted') return 'warning.main';
    if (request.status_display === 'Одобрен') return 'success.main';
    return 'info.main';
  };

  const getStatusBgColor = () => {
    if (request.status === 'fully_accepted') return 'rgba(84, 214, 44, 0.16)';
    if (request.status === 'partially_accepted') return 'rgba(255, 171, 0, 0.16)';
    if (request.status_display === 'Одобрен') return 'rgba(84, 214, 44, 0.16)';
    return 'rgba(24, 144, 255, 0.16)';
  };

  return (
    <Paper 
      elevation={1} 
      sx={{ 
        mb: 2, 
        borderRadius: 2, 
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)'
        },
        '&:active': {
          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.12)'
        }
      }}
      onClick={() => setExpanded(!expanded)}
    >
      {/* Card Header */}
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'divider'
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight="bold">
            #{request.id}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {request.created_at ? formatDate(request.created_at) : request.created_at_formatted}
          </Typography>
          {isFranchise && request.shipping_date && (
            <Typography 
              variant="caption" 
              display="block" 
              sx={{ 
                color: 'primary.main',
                mt: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Typography component="span" variant="caption" fontWeight="bold">{t('Shipping')}:</Typography>
              {formatDateForDisplay(request.shipping_date)}
            </Typography>
          )}
          {isHoreca && request.type_of_delivery && (
            <Typography 
              variant="caption" 
              display="block" 
              sx={{ 
                color: 'primary.main',
                mt: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Typography component="span" variant="caption" fontWeight="bold">{t('Type of Delivery')}:</Typography>
              {t(request.type_of_delivery || '-')}
            </Typography>
          )}
          
          {isHoreca && request.document_type && (
            <Typography 
              variant="caption" 
              display="block" 
              sx={{ 
                color: 'primary.main',
                mt: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Typography component="span" variant="caption" fontWeight="bold">{t('Document Type')}:</Typography>
              {t(request.document_type === 'invoice' ? 'Invoice' : 'Waybill')}
            </Typography>
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              textAlign: 'center',
              color: getStatusColor(),
              bgcolor: getStatusBgColor(),
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              fontSize: '0.75rem',
              whiteSpace: 'nowrap',
              fontWeight: 'medium'
            }}
          >
            {request.status_display}
          </Box>
          
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation(); // Prevent double toggling
              setExpanded(!expanded);
            }}
            sx={{ 
              color: expanded ? '#ec248f' : 'action.active',
              transition: 'transform 0.2s',
              transform: expanded ? 'rotate(180deg)' : 'none'
            }}
          >
            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </Box>
      </Box>
      
      {/* Card Content - Comment */}
      {request.comment && (
        <Box 
          sx={{ 
            px: 2, 
            py: 1, 
            bgcolor: 'rgba(0, 0, 0, 0.02)',
            borderBottom: expanded ? '1px solid' : 'none',
            borderColor: 'divider'
          }}
          onClick={(e) => e.stopPropagation()} // Prevent card expansion when clicking comment
        >
          <Typography variant="body2" color="text.secondary">
            <strong>{t('Comment')}:</strong> {request.comment}
          </Typography>
        </Box>
      )}

      {/* Card Content - Shipping Address */}
      {request.shipping_address && (
        <Box 
          sx={{ 
            px: 2, 
            py: 1, 
            bgcolor: 'rgba(0, 0, 0, 0.02)',
            borderBottom: expanded ? '1px solid' : 'none',
            borderColor: 'divider',
            ...(request.comment ? {} : { borderTop: '1px solid', borderTopColor: 'divider' })
          }}
          onClick={(e) => e.stopPropagation()} // Prevent card expansion when clicking
        >
          <Typography variant="body2" color="text.secondary">
            <strong>{t('Shipping Address')}:</strong> {request.shipping_address}
          </Typography>
        </Box>
      )}
      
      {/* Card Actions */}
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'flex-end',
          borderTop: (request.comment || request.shipping_address) && !expanded ? '1px solid' : 'none',
          borderColor: 'divider'
        }}
      >
        <Button
          startIcon={<ContentCopyIcon />}
          size="small"
          onClick={handleDuplicate}
          sx={{ 
            mr: 1,
            color: '#ec248f',
            borderColor: 'rgba(236, 36, 143, 0.5)',
            '&:hover': { 
              backgroundColor: 'rgba(236, 36, 143, 0.04)',
              borderColor: '#ec248f'
            }
          }}
          variant="outlined"
        >
          {t('Duplicate')}
        </Button>
        {request.status !== 'printed' && (
          <Button
            startIcon={<EditIcon />}
            size="small"
            onClick={handleEdit}
            sx={{ 
              mr: 1,
              color: '#ec248f',
              borderColor: 'rgba(236, 36, 143, 0.5)',
              '&:hover': { 
                backgroundColor: 'rgba(236, 36, 143, 0.04)',
                borderColor: '#ec248f'
              }
            }}
            variant="outlined"
          >
            {t('Edit')}
          </Button>
        )}
        
        {request.status === 'printed' && (
          <Button
            startIcon={<CheckCircleOutlineIcon />}
            size="small"
            onClick={handleAccept}
            color="success"
            variant="outlined"
            sx={{
              mr: 1,
              display: isFranchise || isHoreca ? 'none' : 'inline-flex'
            }}
          >
            {t('Accept')}
          </Button>
        )}
      </Box>
      
      {/* Expanded Content */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Box 
          sx={{ 
            p: 2, 
            bgcolor: 'rgba(236, 36, 143, 0.02)'
          }}
          onClick={(e) => e.stopPropagation()} // Prevent card collapse when clicking items
        >
          <Typography 
            variant="subtitle2" 
            sx={{ 
              fontWeight: 'bold', 
              color: '#ec248f',
              mb: 2
            }}
          >
            {t('Items')}
          </Typography>
          
          {isFranchise && request.shipping_date && (
            <Box 
              sx={{ 
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'primary.light',
                bgcolor: 'rgba(236, 36, 143, 0.04)',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Typography variant="body2">
                <Typography component="span" fontWeight="bold">{t('Shipping Date')}:</Typography> {formatDateForDisplay(request.shipping_date)}
              </Typography>
            </Box>
          )}
          
          {isHoreca && request.type_of_delivery && (
            <Box 
              sx={{ 
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'primary.light',
                bgcolor: 'rgba(236, 36, 143, 0.04)',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Typography variant="body2">
                <Typography component="span" fontWeight="bold">{t('Type of Delivery')}:</Typography> {t(request.type_of_delivery || '-')}
              </Typography>
            </Box>
          )}

          {/* Shipping Address */}
          {request.shipping_address && (
            <Box 
              sx={{ 
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'primary.light',
                bgcolor: 'rgba(236, 36, 143, 0.04)',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Typography variant="body2">
                <Typography component="span" fontWeight="bold">{t('Shipping Address')}:</Typography> {request.shipping_address}
              </Typography>
            </Box>
          )}
          
          {/* Document Type */}
          {isHoreca && request.document_type && (
            <Box 
              sx={{ 
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'primary.light',
                bgcolor: 'rgba(236, 36, 143, 0.04)',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Typography variant="body2">
                <Typography component="span" fontWeight="bold">{t('Document Type')}:</Typography> {t(request.document_type === 'invoice' ? 'Invoice' : 'Waybill')}
              </Typography>
            </Box>
          )}
          
          {request.comment && (
            <Box 
              sx={{ 
                mb: 2,
                p: 1.5,
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'primary.light',
                bgcolor: 'rgba(236, 36, 143, 0.04)',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Typography variant="body2">
                <Typography component="span" fontWeight="bold">{t('Comment')}:</Typography> {request.comment}
              </Typography>
            </Box>
          )}
          
          {Object.values(groupedItems).map((group) => (
            <Box key={`mobile-group-${group.categoryName}`} sx={{ mb: 2 }}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  bgcolor: 'rgba(236, 36, 143, 0.08)',
                  color: 'rgba(236, 36, 143, 0.8)',
                  p: 1,
                  mb: 1,
                  fontWeight: 'bold',
                  borderRadius: 1
                }}
              >
                {group.categoryName}
              </Typography>
              
              {group.items.map((item) => (
                <Box 
                  key={`mobile-item-${item.id || item.product_id}`}
                  sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                    borderBottom: '1px dashed',
                    borderColor: 'divider'
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {item.product_name}
                    </Typography>
                    {item.acceptance_comment && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        {item.acceptance_comment}
                      </Typography>
                    )}
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip 
                      label={item.quantity} 
                      size="small" 
                      variant="outlined"
                      sx={{ 
                        height: 24,
                        fontSize: '0.75rem',
                        borderColor: 'rgba(236, 36, 143, 0.3)',
                        color: '#ec248f'
                      }}
                    />
                    
                    {request.status === 'fully_accepted' || request.status === 'partially_accepted' ? (
                      item.accepted ? (
                        <CheckCircleIcon color="success" fontSize="small" />
                      ) : (
                        <CancelIcon color="error" fontSize="small" />
                      )
                    ) : null}
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

const RequestsList = () => {
  const [requests, setRequests] = useState<UserRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);
  const navigate = useNavigate();
  const { pointOfSale, setPointOfSale, user } = useAuth();
  const isFranchise = user?.type === 'franchise';
  const isHoreca = user?.type === 'horeca';
  const [availablePointsOfSale, setAvailablePointsOfSale] = useState<PointOfSale[]>([]);
  const theme = useTheme();

  useEffect(() => {
    const fetchRequestsData = async (currentPage: number) => {
      if (!pointOfSale) return;
      
      setIsLoading(true);
      try {
        const response = await axiosInstance.get<PaginatedResponse>(
          `/api/v1/user/requests/?page=${currentPage}&page_size=${pageSize}&point_of_sale_id=${pointOfSale.id}`
        );
        
        if (response.data.results) {
          setRequests(response.data.results);
          setTotalPages(Math.ceil(response.data.count / pageSize));
        }
      } catch (error) {
        setRequests([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRequestsData(page);
  }, [page, pageSize, pointOfSale]);

  useEffect(() => {
    const fetchPointsOfSale = async () => {
      try {
        const response = await axiosInstance.get<PointOfSale[]>('/api/v1/user/points-of-sale/');
        setAvailablePointsOfSale(response.data);
        
        // Only set the first point of sale if none is currently selected
        if (!pointOfSale && response.data.length > 0) {
          setPointOfSale(response.data[0]);
        }
      } catch (error) {
        console.error('Error fetching points of sale:', error);
        setAvailablePointsOfSale([]);
      }
    };

    fetchPointsOfSale();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array with eslint disable comment

  const handlePointOfSaleChange = (event: SelectChangeEvent<string | number>) => {
    const selectedId = Number(event.target.value);
    const selectedPos = availablePointsOfSale.find(pos => pos.id === selectedId);
    if (selectedPos) {
      setPointOfSale(selectedPos);
      setPage(1);
    }
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Box 
        sx={{ 
          mb: 4, 
          p: { xs: 2.5, sm: 3 }, 
          borderRadius: 3, 
          background: 'linear-gradient(135deg, #fff0f8 0%, #ffffff 100%)',
          boxShadow: '0 8px 24px rgba(236, 36, 143, 0.08)',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'center', md: 'center' },
          justifyContent: 'space-between',
          gap: { xs: 2, md: 0 },
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 12px 28px rgba(236, 36, 143, 0.12)',
            transform: 'translateY(-2px)'
          }
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          width: { xs: '100%', md: 'auto' },
          justifyContent: { xs: 'left', md: 'flex-start' }
        }}>
          <Avatar
            sx={{
              width: { xs: 60, sm: 70 },
              height: { xs: 60, sm: 70 },
              bgcolor: '#ec248f',
              fontSize: { xs: '1.5rem', sm: '1.8rem' },
              fontWeight: 'bold',
              mr: 2.5,
              boxShadow: '0 4px 12px rgba(236, 36, 143, 0.2)',
              border: '2px solid #fff',
              transition: 'transform 0.3s ease',
              '&:hover': {
                transform: 'scale(1.05)'
              }
            }}
          >
            {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
          </Avatar>
          
          <Box>
            {/* <Typography variant="h5" sx={{ 
              fontWeight: 700, 
              mb: 0.5,
              fontSize: { xs: '1.4rem', sm: '1.5rem' },
              background: 'linear-gradient(45deg, #ec248f, #ff6eb5)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block'
            }}>
              {t('Hello')}, {user?.first_name} {user?.last_name}
            </Typography>
             */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              flexWrap: 'wrap',
              mb: 1 
            }}>
              <Chip 
                label={t(user?.type || '')} 
                size="small"
                sx={{ 
                  borderRadius: '20px',
                  fontWeight: 500,
                  bgcolor: '#ec248f',
                  color: 'white',
                  '& .MuiChip-label': { px: 1.5, py: 0.5 },
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: '#d41e7f',
                    transform: 'translateY(-1px)'
                  }
                }}
              />
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1, 
              flexWrap: 'wrap',
              mt: 0.5
            }}>
              {user?.individual_entrepreneur && (
                <Chip 
                  label={t(user?.individual_entrepreneur || '')} 
                  size="small"
                  variant="outlined"
                  sx={{ 
                    borderRadius: '20px',
                    borderColor: '#ec248f33',
                    color: '#ec248f',
                    '& .MuiChip-label': { px: 1.5 },
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: '#ec248f',
                      backgroundColor: 'rgba(236, 36, 143, 0.05)'
                    }
                  }}
                />
              )}
              <Chip 
                label={user?.phone_number ? formatPhoneNumber(user?.phone_number) : user?.phone_number} 
                size="small" 
                variant="outlined"
                sx={{ 
                  borderRadius: '20px',
                  borderColor: '#ec248f33',
                  color: '#444',
                  '& .MuiChip-label': { px: 1.5, fontWeight: 500 },
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: '#ec248f',
                    backgroundColor: 'rgba(236, 36, 143, 0.05)'
                  }
                }}
              />
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ width: { xs: '100%', md: '40%' } }}>
          <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary', fontWeight: 'medium' }}>
            {t('Point of Sale')}
          </Typography>
          
          <FormControl fullWidth size="small">
            <Select
              value={availablePointsOfSale.length > 0 ? (pointOfSale?.id?.toString() || '') : ''}
              onChange={handlePointOfSaleChange}
              displayEmpty
              sx={{ 
                borderRadius: 2,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.palette.divider
                }
              }}
            >
              {availablePointsOfSale.map((pos) => (
                <MenuItem key={pos.id} value={pos.id.toString()}>
                  {pos.name} - {pos.city}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mb: 4,
        mt: 4
      }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/requests/new')}
          disabled={!pointOfSale}
          sx={{
            borderRadius: '28px',
            px: { xs: 3, sm: 4 },
            py: { xs: 1.2, sm: 1.5 },
            textTransform: 'none',
            fontSize: { xs: '1rem', sm: '1.1rem' },
            fontWeight: 'medium',
            boxShadow: '0 8px 16px rgba(236, 36, 143, 0.2)',
            background: 'linear-gradient(45deg, #ec248f 30%, #ff6eb5 90%)',
            transition: 'all 0.3s ease',
            minWidth: { xs: '200px', sm: '250px' },
            width: { xs: '90%', sm: 'auto' },
            '&:hover': {
              boxShadow: '0 12px 20px rgba(236, 36, 143, 0.3)',
              transform: 'translateY(-2px)'
            },
            '&:active': {
              boxShadow: '0 5px 10px rgba(236, 36, 143, 0.2)',
              transform: 'translateY(1px)'
            },
            '&.Mui-disabled': {
              background: 'linear-gradient(45deg, #9e9e9e 30%, #bdbdbd 90%)',
              boxShadow: 'none'
            }
          }}
        >
          {t('Create New Request')}
        </Button>
      </Box>

      <Box>
        {/* Desktop version - only shown on md and up */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <TableContainer 
            component={Paper}
            sx={{
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
              mb: 4
            }}
          >
            <Table sx={{ 
              '& .MuiTableCell-root': {
                borderColor: 'rgba(224, 224, 224, 0.4)'
              }
            }}>
              <TableHead>
                <TableRow sx={{ 
                  backgroundColor: 'rgba(236, 36, 143, 0.03)',
                  '& th': { 
                    fontWeight: 'bold',
                    color: 'text.primary',
                    fontSize: '0.875rem',
                    py: 2
                  }
                }}>
                  <TableCell sx={{ width: '48px' }} />
                  <TableCell sx={{ width: '80px' }}>{t('ID')}</TableCell>
                  <TableCell sx={{ width: '120px' }}>{t('Status')}</TableCell>
                  <TableCell sx={{ width: '160px' }}>{t('Created At')}</TableCell>
                  {isFranchise && (
                    <TableCell sx={{ width: '160px' }}>{t('Shipping Date')}</TableCell>
                  )}
                  {isHoreca && (
                    <TableCell sx={{ width: '160px' }}>{t('Type of Delivery')}</TableCell>
                  )}
                  <TableCell>{t('Comment')}</TableCell>
                  <TableCell sx={{ width: '100px', textAlign: 'center' }}>{t('Actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={isFranchise || isHoreca ? 8 : 6} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column' }}>
                        <CircularProgress size={40} sx={{ color: '#ec248f', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary">
                          {t('Loading requests...')}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : requests && requests.length > 0 ? (
                  requests.map((request, index) => (
                    <RequestRow 
                      key={request.id || `request-${index}`} 
                      request={request} 
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={isFranchise || isHoreca ? 8 : 6} align="center" sx={{ py: 6 }}>
                      <EmptyStateContent />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Mobile version - only shown on sm and down */}
        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={40} sx={{ color: '#ec248f' }} />
            </Box>
          ) : requests && requests.length > 0 ? (
            <Box sx={{ mb: 4 }}>
              {requests.map((request, index) => (
                <RequestCard 
                  key={request.id || `request-card-${index}`} 
                  request={request} 
                />
              ))}
            </Box>
          ) : (
            <Paper 
              elevation={1} 
              sx={{ 
                p: 4, 
                borderRadius: 2, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                mb: 4
              }}
            >
              <EmptyStateContent />
            </Paper>
          )}
        </Box>
      </Box>

      {/* Pagination */}
      {!isLoading && requests.length > 0 && (
        <Stack spacing={2} sx={{ mt: 3, mb: 3, alignItems: 'center' }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
    </Container>
  );
};

export default RequestsList; 